import { isNumber } from 'lib/axios';
import dayjs from 'lib/dayjs';

// Utility functions for formatting
export const formatCurrency = ({
  value,
  withSign = true,
  supportZero = false,
  currency = '',
  isSymbolPrice = false,
}: {
  value?: string;
  withSign?: boolean;
  supportZero?: boolean;
  currency?: string;
  isSymbolPrice?: boolean;
}): string => {
  if (!value) return `0`;
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return `0`;

  const supportSign = supportZero ? numValue >= 0 : numValue > 0;
  const sign = withSign && supportSign ? '+' : '';
  if (!isSymbolPrice && currency === 'JPY')
    return `${sign}${fixedNumber(numValue, 2)}円`;
  return `${sign}${fixedNumber(numValue, 2)} ${currency}`;
};

export const roundToPrecision = (x: number, d: number): number => {
  if (!d) return x;
  if (!isNumber(x) || isNaN(x)) return 0;
  const factor = Math.pow(10, d);
  return Math.round(x * factor) / factor;
};

export const fixedNumber = (x: number, d: number): string => {
  if (!d) return x.toLocaleString();
  if (!isNumber(x) || isNaN(x)) return '';

  // Prevent javascript floating point problem
  x = roundToPrecision(x, 6);

  x = Math.floor(x * Math.pow(10, d)) / Math.pow(10, d);
  return x
    .toLocaleString(undefined, { maximumFractionDigits: 5 })
    .replace(/\.0+$/, '');
};

export const formatPercent = ({
  value,
  withSign = true,
  supportZero = false,
}: {
  value?: string;
  withSign?: boolean;
  supportZero?: boolean;
}): string => {
  if (!value) return '0.00%';
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0.00%';

  const supportSign = supportZero ? numValue >= 0 : numValue > 0;
  const sign = withSign && supportSign ? '+' : '';
  return `${sign}${fixedNumber(numValue, 2)}%`;
};

export const daysCount = (startDate: string, endDate: string) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day');
};

export const colorByValue = (value: number, defaultValue: string) => {
  if (value > 0) return 'text-growth-green';
  if (value < 0) return 'text-pp-error-primary';
  return defaultValue || 'text-pp-text-primary';
};

export const errorMessageByCode = (code: string) => {
  switch (code) {
    case 'DM-100':
      return '残高が不足しています。';
    case 'DM-101':
      return '保有ポジションがありません。';
    case 'DM-102':
      return '無効な数量です。';
    case 'DM-103':
      return 'マーケット価格を確認できません。';
    case 'DM-104':
      return '取引処理に失敗しました。';
    case 'DM-105':
      return 'マーケットが見つかりませんでした。';
    case 'DM-106':
      return 'ポジションが異なります。';
    case 'DM-107':
      return 'マーケットが閉まっています。';
    case 'DM-108':
      return 'リスク制限を超えています。';
    case 'DM-109':
      return 'ポジションが強制決済リスクにあります。';
    case 'DM-110':
      return 'ユーザーIDが異なります。';
    case 'DM-111':
      return 'マーケットが異なります。';
    case 'DM-112':
      return '注文は１度に資産の20％まで可能です。';
    case 'DM-113':
      return 'ポジションはすでに決済されています。';
    case 'DM-114':
      return 'ポジションタイプが競合しています。';
    case 'DM-115':
      return 'ポジションの上限（30件）に達しているため、新規ポジションを建てられません。';
    case 'DM-120':
      return '認証エラーが発生しました。';
    default:
      return 'エラーが発生しました。';
  }
};
